/**
 * @fileoverview The single USD display formatter for the ParcelPuffin ecosystem.
 * @package @puffin-core/money
 * @module lib/formatUsd
 * @copyright ParcelPuffin 2026
 *
 * ## Why this package exists
 *
 * ES-I18N-FOLLOW-ON Q-11 (2026-08-01). This exact block —
 *
 * ```js
 * new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
 * ```
 *
 * — was found **thirty-one times in pos-web alone**, spread across seven unrelated
 * feature areas, and again in patrol-web, payday-web, purchase-web and console. Every
 * cash-drawer count, receipt total, deposit slip, invoice line and loyalty balance in
 * the ecosystem rendered English-shaped money regardless of app language, and fixing it
 * meant finding every copy.
 *
 * ## Why a package rather than `@puffin-core/page-shell`
 *
 * page-shell already hosts `<CurrencyCell>`, so it looks like the natural home. It is
 * not, for two reasons:
 *
 * 1. **`I18N-BOOTSTRAP-DEPCYCLE-001` lives on that edge.** `useFormatUsd` needs
 *    `useIntlLocale`, and the `page-shell → i18n-bootstrap → page-shell` barrel cycle is
 *    a real module-eval crash that P5 already had to route around with leaf imports.
 *    A mistake there does not fail a test — it fails at module load, in every app at once.
 * 2. **This is not a UI primitive.** `formatUsd` is a pure function with no React
 *    dependency at all; only the optional hook touches React. page-shell is components.
 *
 * A package that depends on nothing but `i18n-bootstrap` has neither problem.
 *
 * ## THIS FILE IS CJS, and that is deliberate (Lane A2, 2026-08-02)
 *
 * The API tier is 100% CommonJS and renders money in three places that are not
 * optional: outbound customer email (`pos-api` order confirmations), the QuickBooks
 * plain-text report builders, and cost-cap error messages. Those callers cannot
 * `import` an ESM-only package, and letting them hand-roll `` `$${v.toFixed(2)}` ``
 * is what produced the 31 copies above.
 *
 * So the implementation lives here in CJS and `lib/esm/formatUsd.js` re-exports it —
 * one implementation, reachable from both module systems. The React hooks
 * (`useFormatUsd*`) stay ESM-only because they depend on `useIntlLocale`, which pulls
 * `react-i18next`; nothing in the API tier wants that, and forcing it across the
 * boundary would drag i18next into every API process. That asymmetry is why this
 * package is DIVERGENT in `@puffin-tooling/esm-build`'s SSOT rather than generated:
 * its ESM surface is its CJS surface **plus** the hooks.
 */

'use strict';

const { getNumberFormatter } = require('@puffin-core/i18n-bootstrap/formatter-cache');

/**
 * The locale for money rendered into a MACHINE or ENGINEER artifact, never onto a
 * user's screen: QuickBooks plain-text report files, CLI operator output, and the
 * cost-cap messages carried by thrown `Error`s.
 *
 * Named, not inlined, for exactly the reason `INVARIANT_PARSE_LOCALE`
 * (`@puffin-core/temporal-core`) is named: the alternative was a path allow-list in
 * the lint rule, and a path fragment exempts a whole directory forever — including a
 * genuine display bug added to it next year. A constant exempts precisely the call
 * that spells it out, and says WHY in the source rather than in a config file.
 *
 * **Do not reach for this to silence a lint rule on something a user reads.** If a
 * person sees the string, it needs the real locale, resolved through `useIntlLocale()`
 * at a component boundary or threaded in as a required parameter.
 *
 * `'en-US'` specifically: these artifacts are consumed by US-based accountants and by
 * engineers reading logs, and `en-US` is guaranteed present in every JS runtime's
 * minimal ICU build.
 *
 * @constant {string}
 */
const INVARIANT_REPORT_LOCALE = 'en-US';

/**
 * The one USD option set.
 * @type {Intl.NumberFormatOptions}
 */
const USD_OPTIONS = Object.freeze({ style: 'currency', currency: 'USD' });

/**
 * Strip a MINUS SIGN from an amount that renders as zero anyway.
 *
 * `Intl.NumberFormat` faithfully signs `-0`, and any negative that rounds to
 * zero at the formatter's precision, producing `-$0` / `-$0.00`. Measured live
 * on PlannerPuffin's Step-4 day grid: **18 occurrences on one screen**, each a
 * day whose planned sales were simply zero. In a column of money a leading minus
 * reads as a real negative, and a negative sales target is not a thing.
 *
 * Fixed HERE rather than at the call sites because this package is the
 * ecosystem's only USD formatter — one guard covers all nine apps and no
 * consumer has to remember it exists.
 *
 * ⚠️ PRECISION-RELATIVE, and that is the whole safety of it. `-0.4` is forty
 * cents to {@link formatUsd} and keeps its sign; it is a rounding artefact to
 * {@link formatUsdWhole} and loses it. A blanket `Object.is(amount, -0)` check
 * would miss the artefacts, and an `Math.abs()` would eat real money.
 *
 * `amount > 0` rather than `>= 0`: in JS `-0 >= 0` is `true`, so the obvious
 * early return would let the one value this function is named for straight
 * through.
 *
 * @private
 * @param {number} amount
 * @param {number} fractionDigits - The formatter's `maximumFractionDigits`.
 * @returns {number} `amount`, or `0` when it renders as zero at that precision.
 */
function withoutNegativeZero(amount, fractionDigits) {
  // Only a NUMBER is normalised; anything else (null, undefined, a string) reaches `Intl`
  // untouched so the call-site coercion doctrine above holds. NaN / ±Infinity need no guard:
  // they fall through the arithmetic to themselves (`Math.round(NaN) === 0` is false), and a
  // positive amount is never a rounded zero — both former short-circuits were equivalent
  // mutants (QUALITY-PLATFORM mutation lane, 2026-08-24), i.e. dead code. Value-level note: a
  // positive sub-cent amount (0.001 at 2 digits) now RETURNS 0 rather than itself; Intl renders
  // both as `$0.00`, which is exactly why the guard was equivalent — do not re-add it "for safety".
  if (typeof amount !== 'number') return amount;
  const factor = 10 ** fractionDigits;
  return Math.round(amount * factor) === 0 ? 0 : amount;
}

/**
 * Format an amount as USD for display.
 *
 * @param {number} amount - The amount. This helper does **not** coerce a nullish input,
 *   because roughly half the thirty-one copies it replaced wrote `.format(amount || 0)`
 *   and half wrote `.format(amount)`. Picking one behaviour for everybody would have
 *   silently changed what the other half renders, so the coercion stays at the call
 *   sites. `Intl` formats `null` as `$0.00` and `undefined` as `$NaN` — the distinction
 *   is real, and a test pins it.
 * @param {string} locale - BCP-47 tag, from `useIntlLocale()` at a component boundary or
 *   threaded in as a required parameter by a pure helper. There is deliberately **no
 *   default**: a silent `'en-US'` is the bug this seam exists to remove. Machine/engineer
 *   artifacts pass {@link INVARIANT_REPORT_LOCALE} explicitly.
 * @returns {string} e.g. `'$1,234.56'`.
 * @throws {TypeError} When `locale` is absent.
 */
function formatUsd(amount, locale) {
  return getNumberFormatter(locale, USD_OPTIONS).format(withoutNegativeZero(amount, 2));
}

/**
 * The ONE mark for an amount nobody can read (ZERO-DEFAULT MONEY, PAYDAY-RUN-LIFECYCLE-001, 2026-10-19): an em dash,
 * the glyph page-shell's `<CurrencyCell>` renders for the same case, which imports it from here so the two cannot
 * drift. An unreadable amount is NEVER rendered as `$0.00`: that tells a person "nothing is owed" when the truth is
 * "we do not know".
 *
 * @constant {string}
 */
const ABSENT_AMOUNT = '—';

/**
 * The finite number an amount holds, or `null` when it holds none. A number must be finite; a STRING is read as a
 * number when it is numeric (PostgreSQL answers `NUMERIC` / `DECIMAL` columns as strings so precision survives the
 * wire). `null`, `undefined`, `''`, whitespace, a word, `NaN` and ±Infinity hold no amount. The ONE reading of "can this
 * amount be shown", shared with `<CurrencyCell>`.
 *
 * @param {*} value
 * @returns {number|null}
 */
function readableAmount(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Format an amount as USD, or {@link ABSENT_AMOUNT} when it cannot be read — never `$0.00` for "unknown".
 *
 * The display remedy for a parse defaulted to zero (`formatUsd(Number(x) || 0, locale)`): pass the raw value here
 * instead. A numeric string formats; anything {@link readableAmount} cannot read renders the absent mark.
 *
 * @param {number|string|null|undefined} value - the amount as it arrived (a number, or a numeric string)
 * @param {string} locale - BCP-47 tag, REQUIRED as {@link formatUsd} requires it, and refused even when the amount is
 *   absent, so a missing locale never hides behind missing data.
 * @returns {string} e.g. `'$1,234.56'`, or `'—'`.
 * @throws {TypeError} When `locale` is absent.
 */
function formatUsdOrAbsent(value, locale) {
  const formatter = getNumberFormatter(locale, USD_OPTIONS);
  const amount = readableAmount(value);
  return amount === null ? ABSENT_AMOUNT : formatter.format(withoutNegativeZero(amount, 2));
}

/**
 * The whole-dollar option set — cents suppressed, not rounded away by the caller.
 * @type {Intl.NumberFormatOptions}
 */
const USD_WHOLE_OPTIONS = Object.freeze({
  ...USD_OPTIONS,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format an amount as whole-dollar USD — `'$1,235'`, no cents.
 *
 * A **second named export rather than an options parameter** on `formatUsd`. An options
 * bag would let every call site invent its own currency shape again, which is the
 * fragmentation this package was built to end; two named intents cover every site found
 * in the sweep (KPI hero strips and variance deltas want whole dollars, everything else
 * wants cents) and a third would have to justify itself.
 *
 * Rounding is Intl's, so `1234.5` renders `'$1,235'` — callers must not pre-round. The
 * console KPI helper used to write `` `$${fmt.format(Math.round(cents / 100))}` ``, which
 * double-rounds and hard-codes the affix.
 *
 * @param {number} amount - Dollars (not cents). Nullish is not coerced — see `formatUsd`.
 * @param {string} locale - BCP-47 tag from the i18n seam. No default, by design.
 * @returns {string} e.g. `'$1,235'`.
 * @throws {TypeError} When `locale` is absent.
 */
function formatUsdWhole(amount, locale) {
  return getNumberFormatter(locale, USD_WHOLE_OPTIONS).format(withoutNegativeZero(amount, 0));
}

/**
 * Format an amount as whole-dollar USD, or {@link ABSENT_AMOUNT} when it cannot be read — the whole-dollar twin of
 * {@link formatUsdOrAbsent}, for the grids and tier rows that render money without cents (PLN-SP-UAT-005 B3 / D2,
 * owner ruling R-UAT-18: ONE absent rule, shared with the cents formatter through {@link readableAmount}). A planner
 * grid used to read its cell with `parseFloat` and dash only a non-finite result, so `'12abc'` printed `$12`: a
 * malformed string holds no amount, and renders the mark.
 *
 * @param {number|string|null|undefined} value - the amount as it arrived (a number, or a numeric string)
 * @param {string} locale - BCP-47 tag, REQUIRED as {@link formatUsdWhole} requires it, and refused even when the
 *   amount is absent.
 * @returns {string} e.g. `'$1,235'`, or `'—'`.
 * @throws {TypeError} When `locale` is absent.
 */
function formatUsdWholeOrAbsent(value, locale) {
  const formatter = getNumberFormatter(locale, USD_WHOLE_OPTIONS);
  const amount = readableAmount(value);
  return amount === null ? ABSENT_AMOUNT : formatter.format(withoutNegativeZero(amount, 0));
}

/**
 * Format an integer-cents amount as USD for display — `formatUsd(cents / 100)`.
 *
 * The division looks trivial enough to leave at the call sites, and that is exactly
 * why it had been written **eleven** separate times across purchase-web and port-web
 * as `` `$${(cents / 100).toFixed(2)}` ``, plus twice more with the division spelled
 * differently. Each copy carried the same two defects (hardcoded `$` affix, no
 * grouping) and each had to be found individually. Naming the conversion is what
 * makes "money that arrives in cents" a single decision instead of a habit.
 *
 * @param {number|string} cents - Integer cents. An amount nobody can read renders {@link ABSENT_AMOUNT}. This
 *   docblock used to say nullish was "not coerced", but `null / 100` is 0 and printed `$0.00`, and `undefined / 100`
 *   printed `$NaN` (ZERO-DEFAULT MONEY, 2026-10-19).
 * @param {string} locale - BCP-47 tag from the i18n seam. No default, by design.
 * @returns {string} e.g. `'$12.34'`, or `'—'` when unreadable.
 * @throws {TypeError} When `locale` is absent.
 */
function formatUsdFromCents(cents, locale) {
  const amount = readableAmount(cents);
  return amount === null ? formatUsdOrAbsent(null, locale) : formatUsd(amount / 100, locale);
}

/**
 * Four decimals: sub-cent amounts are routine for AI/token pricing.
 * @type {Intl.NumberFormatOptions}
 */
const CENT_OPTIONS = Object.freeze({ minimumFractionDigits: 4, maximumFractionDigits: 4 });

/**
 * Format an amount that is genuinely SUB-CENT as cents — `'1.2300¢'`.
 *
 * ## Why a cent formatter exists at all
 *
 * This is Q-13's ruling, promoted (ES-I18N-FOLLOW-ON Q-19, 2026-08-02). AI and token
 * pricing is quoted in fractions of a cent, and rendering it as dollars-2dp collapses the
 * entire signal: `$0.0123` becomes `$0.01`, and a COST-CAP message degrades to the useless
 * *"accumulated $0.01 >= cap $0.01"*. Q-13 hit this in port-web's AI-cost column and ruled
 * **keep the unit, fix the marker** — so the value stays in cents and carries a `¢`.
 *
 * It lives here rather than staying port-web-local (where Q-13 first implemented it)
 * because the same need appeared six more times in the API tier — `voice-communication`'s
 * cost caps and `prep-api`'s generation scripts. One implementation, both module systems.
 *
 * **This is NOT a general money formatter.** Anything a customer reads as a price uses
 * {@link formatUsd}. Reach for this only where sub-cent precision is the point.
 *
 * @param {number|string} cents - The amount in CENTS (a caller holding dollars passes `usd * 100`).
 *   An amount nobody can read renders {@link ABSENT_AMOUNT}: never `NaN¢`, which reads as a billing fault, and never
 *   `0.0000¢`, which tells a person the cost was nothing (it rendered that until ZERO-DEFAULT MONEY, 2026-10-19).
 * @param {string} locale - BCP-47 tag. No default, by design — machine/engineer artifacts
 *   pass {@link INVARIANT_REPORT_LOCALE} explicitly.
 * @returns {string} e.g. `'1.2300¢'`, or `'—'` when unreadable.
 * @throws {TypeError} When `locale` is absent.
 */
function formatCents(cents, locale) {
  const formatter = getNumberFormatter(locale, CENT_OPTIONS);
  const amount = readableAmount(cents);
  return amount === null ? ABSENT_AMOUNT : `${formatter.format(amount)}¢`;
}

/**
 * Format a DOLLAR amount that is genuinely sub-cent, as cents — `formatCents(usd * 100)`.
 *
 * The six API-tier sites this was built for all hold USD (`totalCostUsd`), while Q-13's
 * original held cents. Naming the conversion keeps `* 100` from being written six times,
 * which is precisely how `formatUsdFromCents` earned its place.
 *
 * @param {number|string} usd - The amount in dollars.
 * @param {string} locale - BCP-47 tag. No default, by design.
 * @returns {string} e.g. `'1.2300¢'` for `0.0123`, or `'—'` when unreadable.
 * @throws {TypeError} When `locale` is absent.
 */
function formatCentsFromUsd(usd, locale) {
  const amount = readableAmount(usd);
  return formatCents(amount === null ? null : amount * 100, locale);
}

module.exports = {
  formatUsd,
  formatUsdOrAbsent,
  readableAmount,
  ABSENT_AMOUNT,
  formatUsdWhole,
  formatUsdWholeOrAbsent,
  formatUsdFromCents,
  formatCents,
  formatCentsFromUsd,
  INVARIANT_REPORT_LOCALE,
};
